import { ConfidentialIceCandidate } from '../helpers/confidential';
import { UnboundedFlowControlledDataChannel } from '../helpers/data_channel';
export class PeerConnectionHelper {
    static CONNECTION_FAILED_TIMEOUT_MS = 15000;
    log;
    $q;
    $rootScope;
    userConfig;
    logService;
    timeoutService;
    pc;
    webrtcTask;
    connectionFailedTimer = null;
    sdc = null;
    closed = false;
    connectionState = "new";
    onConnectionStateChange = null;
    constructor($q, $rootScope, userConfig, logService, timeoutService, webrtcTask, iceServers) {
        this.log = logService.getLogger('PeerConnection', 'color: #fff; background-color: #3333ff');
        this.log.info('Initialize WebRTC PeerConnection');
        this.log.debug('ICE servers used:', [].concat(...iceServers.map((server) => server.urls)));
        this.$q = $q;
        this.$rootScope = $rootScope;
        this.userConfig = userConfig;
        this.logService = logService;
        this.timeoutService = timeoutService;
        this.webrtcTask = webrtcTask;
        this.pc = new RTCPeerConnection({ iceServers: iceServers });
        this.pc.onnegotiationneeded = () => {
            this.log.debug('RTCPeerConnection: negotiation needed');
            this.initiatorFlow()
                .then(() => this.log.debug('Initiator flow done'));
        };
        this.pc.onconnectionstatechange = () => {
            this.log.debug('Connection state change:', this.pc.connectionState);
        };
        this.pc.onsignalingstatechange = () => {
            this.log.debug('Signaling state change:', this.pc.signalingState);
        };
        this.setupIceCandidateHandling();
        this.pc.ondatachannel = (e) => {
            this.log.debug('New data channel was created:', e.channel.label);
        };
    }
    get peerConnection() {
        return this.pc;
    }
    setupIceCandidateHandling() {
        this.log.debug('Setting up ICE candidate handling');
        this.pc.onicecandidate = (e) => {
            if (e.candidate) {
                this.log.debug('Gathered local ICE candidate:', new ConfidentialIceCandidate(e.candidate.candidate));
                this.webrtcTask.sendCandidate({
                    candidate: e.candidate.candidate,
                    sdpMid: e.candidate.sdpMid,
                    sdpMLineIndex: e.candidate.sdpMLineIndex,
                });
            }
            else {
                this.log.debug('No more local ICE candidates');
            }
        };
        this.pc.onicecandidateerror = (e) => {
            this.log.warn(`ICE candidate error: ${e.errorText} ` +
                `(url=${e.url}, host-candidate=${e.hostCandidate}, code=${e.errorCode})`);
        };
        this.pc.oniceconnectionstatechange = () => {
            this.log.debug('ICE connection state change:', this.pc.iceConnectionState);
            this.$rootScope.$apply(() => {
                if (this.connectionFailedTimer !== null) {
                    this.timeoutService.cancel(this.connectionFailedTimer);
                    this.connectionFailedTimer = null;
                }
                switch (this.pc.iceConnectionState) {
                    case 'new':
                        this.setConnectionState("new");
                        break;
                    case 'checking':
                    case 'disconnected':
                        this.setConnectionState("connecting");
                        this.connectionFailedTimer = this.timeoutService.register(() => {
                            this.connectionFailedTimer = null;
                            this.log.debug('ICE connection considered failed');
                            this.closed = true;
                            this.close();
                            this.setConnectionState("disconnected");
                        }, PeerConnectionHelper.CONNECTION_FAILED_TIMEOUT_MS, true, 'connectionFailedTimer');
                        break;
                    case 'connected':
                    case 'completed':
                        this.setConnectionState("connected");
                        break;
                    case 'failed':
                    case 'closed':
                        this.closed = true;
                        this.setConnectionState("disconnected");
                        break;
                    default:
                        this.log.warn('Ignored ICE connection state change to', this.pc.iceConnectionState);
                }
            });
        };
        this.pc.onicegatheringstatechange = () => {
            this.log.debug('ICE gathering state change:', this.pc.iceGatheringState);
        };
        this.webrtcTask.on('candidates', (e) => {
            for (const candidateInit of e.data) {
                if (candidateInit) {
                    this.log.debug('Adding remote ICE candidate:', new ConfidentialIceCandidate(candidateInit.candidate));
                }
                else {
                    this.log.debug('No more remote ICE candidates');
                }
                this.pc.addIceCandidate(candidateInit)
                    .catch((error) => this.log.warn('Unable to add ice candidate:', error));
            }
        });
    }
    async initiatorFlow() {
        const offer = await this.pc.createOffer();
        await this.pc.setLocalDescription(offer);
        this.log.debug('Created offer, set local description');
        this.webrtcTask.sendOffer(offer);
        const receiveAnswer = () => {
            return new Promise((resolve) => {
                this.webrtcTask.once('answer', (e) => {
                    resolve(e.data);
                });
            });
        };
        const answer = await receiveAnswer();
        await this.pc.setRemoteDescription(answer);
        this.log.debug('Received answer, set remote description');
    }
    handover() {
        if (this.sdc !== null) {
            throw new Error('Handover already inintiated');
        }
        const link = this.webrtcTask.getTransportLink();
        const dc = this.pc.createDataChannel(link.label, {
            id: link.id,
            negotiated: true,
            ordered: true,
            protocol: link.protocol,
        });
        dc.binaryType = 'arraybuffer';
        this.sdc = new UnboundedFlowControlledDataChannel(dc, this.logService, this.userConfig.TRANSPORT_LOG_LEVEL);
        const self = this;
        const handler = {
            get maxMessageSize() {
                return self.pc.sctp.maxMessageSize;
            },
            close() {
                if (self.closed) {
                    self.log.debug('Ignoring signalling data channel close request, already closed');
                }
                else {
                    self.log.debug(`Signalling data channel close request`);
                    dc.close();
                }
            },
            send(message) {
                self.log.debug(`Signalling data channel outgoing signaling message of ` +
                    `length ${message.byteLength}`);
                self.sdc.write(message);
            },
        };
        dc.onopen = () => {
            this.log.info(`Signalling data channel open`);
            dc.onclose = () => {
                if (this.closed) {
                    this.log.debug('Ignoring signalling data channel closed');
                }
                else {
                    this.log.info(`Signalling data channel closed`);
                    link.closed();
                }
            };
            this.webrtcTask.handover(handler);
        };
        dc.onclose = () => {
            this.log.error(`Signalling data channel closed`);
        };
        dc.onerror = (event) => {
            this.log.warn(`Signalling data channel error (closed=${this.closed}):`, event.error);
        };
        dc.onmessage = (event) => {
            this.log.debug(`Signalling data channel incoming message of length ${event.data.byteLength}`);
            link.receive(new Uint8Array(event.data));
        };
    }
    setConnectionState(state) {
        if (state !== this.connectionState) {
            this.connectionState = state;
            if (this.onConnectionStateChange !== null) {
                this.onConnectionStateChange(state);
            }
        }
    }
    close() {
        if (this.connectionFailedTimer !== null) {
            this.timeoutService.cancel(this.connectionFailedTimer);
            this.connectionFailedTimer = null;
        }
        this.webrtcTask.off();
        this.pc.onnegotiationneeded = null;
        this.pc.onconnectionstatechange = null;
        this.pc.onsignalingstatechange = null;
        this.pc.onicecandidate = null;
        this.pc.onicecandidateerror = null;
        this.pc.oniceconnectionstatechange = null;
        this.pc.onicegatheringstatechange = null;
        this.pc.ondatachannel = null;
        this.pc.close();
    }
}
